import {EMPTY, Observable, defer, of} from 'rxjs';
import {expand, map, reduce, tap} from 'rxjs/operators';
import {Injectable, isDevMode} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {BaseService} from '@shared/services/api/base-service';
import {PagedModel} from '../../api/model/paged-model';
import VariableOptions from 'gql-query-builder/build/VariableOptions';
import {ObjectUtils} from '@app/shared/util/general/object-utils';

export enum GRAPHQL_TYPES {
  STRING = 'String',
  INTEGER = 'Int',
  INTEGER_ARRAY = '[Int]',
  LONG = 'Long',
  LONG_ARRAY = '[Long]',
  BOOLEAN = 'Boolean',
  DATE_TIME = 'DateTime',
  UUID = 'UUID',
  LOCAL_DATE_TIME = 'LocalDateTime',
  SORT_ORDER_ENUM = 'SortOrderEnum',
  DOUBLE = 'Double',
  INTERVAL = 'Interval',
  LOCAL_INTERVAL = 'LocalInterval',
  LOCAL_DATE = 'LocalDate',
  LOCAL_DATE_INTERVAL = 'LocalDateInterval',
  SUSTAINABILITY_RATINGS_AGGREGATE_ENTITY_ENUM = 'SustainabilityRatingsAggregateEntityEnum',
  RULE_STATE = 'RuleState',
  FAULT_STATUS = 'FaultStatus'
}

export interface GQLQuery {
  variables: any;
  query: string;
}

/** This is the usual paging params used in our api (rest & graphql) except for ticketd.
 * TODO: KQ: make all interface (except ticketd until it is updated) for request with paging use this. */
export interface PagingParams {
  start_index?: number;
  limit?: number;
  no_count?: boolean;
}

export type GraphQlVariables = {[key: string]: VariableOptions};

export const baseGqlPagingParams: GraphQlVariables = {
  limit: {type: GRAPHQL_TYPES.INTEGER},
};

export const GQLPagingParamsVariableStartIndexRequiredWithoutNoCount: GraphQlVariables = {
  ...baseGqlPagingParams,
  start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
};

export const GQLPagingParamsVariableStartIndexRequired: GraphQlVariables = {
  ...GQLPagingParamsVariableStartIndexRequiredWithoutNoCount,
  no_count: {type: GRAPHQL_TYPES.BOOLEAN}
};

export const GQLPagingParamsVariableWithoutNoCount: GraphQlVariables = {
  ...baseGqlPagingParams,
  start_index: {type: GRAPHQL_TYPES.INTEGER},
};

/** TODO: KQ: make all graphql variables reuse this. */
export const GQLPagingParamsVariable: GraphQlVariables = {
  ...GQLPagingParamsVariableWithoutNoCount,
  no_count: {type: GRAPHQL_TYPES.BOOLEAN}
};

export interface APIPageInfo {
  first: number;
  rows: number;
}

export namespace APIPageInfo {
  export function build(startIndex: number, limit: number): APIPageInfo {
    return {first: startIndex, rows: limit};
  }
}


export type PagedModelFetchFn<T> = (pageInfo: APIPageInfo) => Observable<PagedModel<T>>;


@Injectable({
  providedIn: 'root'
})
export class HttpUtilityService extends BaseService {

  public static readonly QUERY_PARAMS_LIMIT = 30000;

  private readonly logger: AceLogger;
  private readonly TICKETS_PATH = '/tickets';
  private readonly TASKS_PATH = '/tasks';
  private readonly FILES_PATH = '/files';
  private readonly GRAPHQL_PATH = '/graphql';

  constructor(private http: HttpClient, logService: AceLoggerService) {
    super();
    this.logger = logService.create(HttpUtilityService.name);
  }

  public isTimeoutError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 504;
  }

  public pageThrough<T>(callback: PagedModelFetchFn<T>, pageInfo: APIPageInfo): Observable<PagedModel<T>> {
    return this.pageUntil(callback, pageInfo, Number.POSITIVE_INFINITY);
  }

  public pageUntilComplete<T>(callback: PagedModelFetchFn<T>, pageInfo: APIPageInfo): Observable<T> {
    return this.pageUntilAndCollect(callback, pageInfo, Number.POSITIVE_INFINITY);
  }

  public pageUntilLimit<T>(callback: PagedModelFetchFn<T>,
                           pageInfo: APIPageInfo,
                           limit: number): Observable<PagedModel<T>> {
    const pageInfoClone = {...pageInfo};
    return defer(() => {
      let totalCount = 0;
      const wrappedCallback = (pageInfo: APIPageInfo) => callback(pageInfo).pipe(tap(({ count }) => totalCount = count));
      return this.pageUntilAndCollect(wrappedCallback, pageInfoClone, limit).pipe(map(data => ({count: totalCount, data})));
    })
  }

  private pageUntilAndCollect<T>(callback: PagedModelFetchFn<T>,
                                 pageInfo: APIPageInfo,
                                 limit: number): Observable<T> {
    return this.pageUntil(callback, pageInfo, limit).pipe(
      reduce((acc: any, x: PagedModel<T>) => acc.concat(x.data), []),
    );
  }

  private pageUntil<T>(callback: PagedModelFetchFn<T>,
                       initialPageInfo: APIPageInfo,
                       limit: number): Observable<PagedModel<T>> {
    const initialPageInfoClone = {...initialPageInfo};
    return defer(() => {
      let pageInfo = {...initialPageInfoClone};
      let totalFetched = 0;
      let nextPageSize = Math.min(pageInfo.rows, limit);

      return callback({ first: pageInfo.first, rows: nextPageSize }).pipe(
        expand(({ count: count, data: data }: PagedModel<any>) => {
          let nextObservable: Observable<PagedModel<T>> = EMPTY;
          pageInfo.first = pageInfo.first + nextPageSize;
          if (data?.length >= pageInfo.rows) {
            totalFetched += data.length;
            nextPageSize = Math.min(pageInfo.rows, limit - totalFetched);
            if (totalFetched < Math.min(count == null ? Number.POSITIVE_INFINITY : count, limit)) {
              nextObservable = callback({ first: pageInfo.first, rows: nextPageSize });
            }
          }
          return nextObservable;
        }));
    });
  }

  public requestPartitioned<K, T>(callback: (items: K[]) => Observable<T[]>, parameters: K[], partitionSize: number): Observable<T[]> {
    if (partitionSize <= 0) {
      throw new Error('Partition size must be greater than 0. Got: ' + partitionSize);
    }

    if (parameters && parameters.length) {
      let i = 0;
      let end = Math.min(partitionSize, parameters.length);
      return callback(parameters.slice(i, end)).pipe(
        expand(() => {
          i = end;
          if (i < parameters.length) {
            end = Math.min(i + partitionSize, parameters.length);
            return callback(parameters.slice(i, end));
          } else {
            return EMPTY;
          }
        }),
        reduce((acc: T[], data: T[]) => data && data.length ? acc.concat(data) : acc, [])
      );
    } else {
      return of([]);
    }
  }

  private makeNonPagedGraphQLRequest<T>(query: GQLQuery, gateway: string, responseProperty: string = null, variables = null): Observable<T> {
    this.validateGraphqlVariables(variables, query.variables);
    const options = { headers: this.getHeaders(), params: {graphql_operation: responseProperty} };
    const fullQuery = JSON.stringify({ query: query.query, operationName: responseProperty, variables: Object.assign({}, query.variables, variables) });
    return this.http.post<T>(gateway + this.GRAPHQL_PATH, fullQuery, options);
  }

  private executePagedGQL<T>(query: GQLQuery, gateway: string, responseProperty: string = null, variables = null): Observable<PagedModel<T>> {
    return this.makeNonPagedGraphQLRequest<T>(query, gateway, responseProperty, variables)
      .pipe(map(response => this.extractPagedResponse<T>(response, responseProperty)));
  }

  private executeNonPagedGQL<T>(query: GQLQuery, gateway: string, responseProperty: string = null, variables = null): Observable<T> {
    return this.makeNonPagedGraphQLRequest<T>(query, gateway, responseProperty, variables)
      .pipe(map(response => this.extractResponseData<T>(response, responseProperty)));
  }

  private executePagedGraphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null, gatewayPathSuffix?: string): Observable<PagedModel<T>> {
    return this.executePagedGQL<T>(query, this.getGateway() + (gatewayPathSuffix ?? ''), responseProperty, variables);
  }

  private executeNonPagedGraphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null, gatewayPathSuffix?: string): Observable<T> {
    return this.executeNonPagedGQL<T>(query, this.getGateway() + (gatewayPathSuffix ?? ''), responseProperty, variables);
  }

  public graphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null): Observable<PagedModel<T>> {
    return this.executePagedGraphQL<T>(query, responseProperty, variables);
  }

  public ticketsGraphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null): Observable<PagedModel<T>> {
    return this.executePagedGraphQL<T>(query, responseProperty, variables, this.TICKETS_PATH);
  }

  public tasksGraphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null): Observable<PagedModel<T>> {
    return this.executePagedGraphQL<T>(query, responseProperty, variables, this.TASKS_PATH);
  }

  public filesGraphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null): Observable<PagedModel<T>> {
    return this.executePagedGraphQL<T>(query, responseProperty, variables, this.FILES_PATH);
  }

  public nonPagedGraphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null): Observable<T> {
    return this.executeNonPagedGraphQL<T>(query, responseProperty, variables);
  }

  public nonPagedTasksGraphQL<T>(query: GQLQuery, responseProperty: string = null, variables = null): Observable<T> {
    return this.executeNonPagedGraphQL<T>(query, responseProperty, variables, this.TASKS_PATH);
  }

  public extractPagedResponse<T>(response: { [key: string]: any }, responseProperty: string): PagedModel<T> {
    const responseHasFieldAggregates = (this.METADATA_KEY in response && this.FIELD_AGGREGATES_KEY in response[this.METADATA_KEY]);
    return {
      data: this.extractResponseData<T>(response, responseProperty),
      count: this.extractRecordCountFromResponse(response),
      ...responseHasFieldAggregates && { [this.FIELD_AGGREGATES_KEY]: response[this.METADATA_KEY][this.FIELD_AGGREGATES_KEY] }
    };
  }

  public extractResponseDataCompat<T>(response: { [key: string]: any }, responseProperty: string): T {
    if (response) {
      const data = response[responseProperty];
      return data ?? this.extractResponseData(response, responseProperty);
    } else {
      throw new Error('Expected response but received ' + JSON.stringify(response));
    }
  }

  public extractResponseDataMultiKeyCompat<T>(response: {[key: string]: any}, responseProperty: string): T {
    if (response) {
      const responseData = response[this.DATA_PREFIX];
      if (responseData) {
        if (Object.keys(responseData).some(key => key !== responseProperty)) {
          // multiple keys in payload
          return responseData;
        } else {
          const expectedData = responseData[responseProperty];
          if (expectedData) {
            return expectedData;
          } else {
            throw new Error('Expected response with "' + responseProperty + '" property but received ' + JSON.stringify(response));
          }
        }
      } else if (response[this.ERRORS_PREFIX]) {
        throw new Error(JSON.stringify(response[this.ERRORS_PREFIX]));
      } else {
        throw new Error('Expected response with "' + this.DATA_PREFIX + '" property but received ' + JSON.stringify(response));
      }
    } else {
      throw new Error('Expected response but received ' + JSON.stringify(response));
    }
  }

  public extractResponseData<T>(response: { [key: string]: any }, responseProperty: string): T {
    if (response) {
      const data = response[this.DATA_PREFIX];
      if (data) {
        const result = data[responseProperty];
        if (result) {
          return result;
        } else {
          throw new Error('Expected response with "' + responseProperty + '" property but received ' + JSON.stringify(response));
        }
      } else if (response[this.ERRORS_PREFIX]) {
        throw new Error(JSON.stringify(response[this.ERRORS_PREFIX]));
      } else {
        throw new Error('Expected response with "' + this.DATA_PREFIX + '" property but received ' + JSON.stringify(response));
      }
    } else {
      throw new Error('Expected response but received ' + JSON.stringify(response));
    }
  }

  public extractRecordCountFromResponse(response: { [key: string]: any }): number {
    const responseMetadata = response[this.METADATA_KEY];
    if (responseMetadata) {
      const recordCount = responseMetadata[this.RECORD_COUNT_KEY];
      if (recordCount != null) {
        return recordCount;
      } else {
        throw new Error('Expected response with "' + this.RECORD_COUNT_KEY + '" property but received ' + JSON.stringify(response));
      }
    } else {
      throw new Error('Expected response with "' + this.METADATA_KEY + '" property but received ' + JSON.stringify(response));
    }
  }

  private validateGraphqlVariables<T>(variables: T, variableOptions: Record<string, VariableOptions>): void {
    if (variables) {
      const missingPart = Object.keys(variables).reduce((missingPart, key) => {
        if (!Object.hasOwn(variableOptions, key)) {
          missingPart[key] = variables[key];
        }
        return missingPart;
      }, {});
      if (!ObjectUtils.isEmpty(missingPart)) {
        const message = 'Some variable properties do not exist on the variable options';
        if (isDevMode()) {
          throw new Error(message + ': ' + JSON.stringify(missingPart));
        } else {
          this.logger.error(message, missingPart);
        }
      }
    }
  }
}
